import { useCallback, useEffect, useRef } from "react";

type MotionPermissionState = "granted" | "denied" | "prompt";

export type UseShakeOptions = {
  /**
   * Shake algılama eşiği (g-force).
   * Tipik aralık: 2.0 - 3.5
   */
  thresholdG?: number;
  /**
   * Shake'i tetiklemek için gerekli minimum eşik üstü sarsıntı sayısı.
   * 1: tek sarsıntı yeterli (default, geriye uyumlu)
   */
  minShakeCount?: number;
  /**
   * minShakeCount için zaman penceresi (ms). Bu süre içinde eşik üstü sarsıntılar sayılır.
   */
  windowMs?: number;
  /**
   * Arka arkaya tetiklemeyi engellemek için minimum süre (ms).
   */
  cooldownMs?: number;
  /**
   * Listener aktif/pasif.
   */
  enabled?: boolean;
  /**
   * Event yakalandığında çalıştırılacak callback.
   */
  onShake: () => void;
};

export type UseShakeReturn = {
  /**
   * iOS Safari (özellikle iOS 13+) için motion iznini istemek gerekebilir.
   * Bu fonksiyonu kullanıcı etkileşimi (button click) içinde çağırın.
   */
  requestPermission: () => Promise<MotionPermissionState>;
  /**
   * Cihaz motion event'leri destekliyor mu?
   */
  isSupported: boolean;
};

const GRAVITY = 9.80665;

function computeGForce(
  x: number | null | undefined,
  y: number | null | undefined,
  z: number | null | undefined
) {
  const ax = x ?? 0;
  const ay = y ?? 0;
  const az = z ?? 0;
  const magnitude = Math.sqrt(ax * ax + ay * ay + az * az);
  return magnitude / GRAVITY;
}

/**
 * iOS Safari için motion permission isteme helper'ı.
 * Android/Chrome'da çoğunlukla otomatik çalışır ve "granted" döner.
 */
export async function requestMotionPermission(): Promise<MotionPermissionState> {
  if (typeof window === "undefined") return "denied";

  const DeviceMotionEventAny = DeviceMotionEvent as unknown as {
    requestPermission?: () => Promise<MotionPermissionState>;
  };

  if (typeof DeviceMotionEventAny?.requestPermission === "function") {
    try {
      return await DeviceMotionEventAny.requestPermission();
    } catch {
      return "denied";
    }
  }

  // requestPermission yoksa genelde permission gerekmiyordur.
  return "granted";
}

export function useShake({
  thresholdG = 2.7,
  minShakeCount = 1,
  windowMs = 400,
  cooldownMs = 900,
  enabled = true,
  onShake,
}: UseShakeOptions): UseShakeReturn {
  const lastShakeAtRef = useRef<number>(0);
  const shakeHitsRef = useRef<number[]>([]);
  const onShakeRef = useRef(onShake);
  const thresholdRef = useRef(thresholdG);
  const minShakeCountRef = useRef(minShakeCount);
  const windowMsRef = useRef(windowMs);
  const cooldownRef = useRef(cooldownMs);

  useEffect(() => {
    onShakeRef.current = onShake;
  }, [onShake]);

  useEffect(() => {
    thresholdRef.current = thresholdG;
  }, [thresholdG]);

  useEffect(() => {
    minShakeCountRef.current = minShakeCount;
  }, [minShakeCount]);

  useEffect(() => {
    windowMsRef.current = windowMs;
  }, [windowMs]);

  useEffect(() => {
    cooldownRef.current = cooldownMs;
  }, [cooldownMs]);

  const isSupported =
    typeof window !== "undefined" &&
    typeof window.addEventListener === "function";

  const handleMotion = useCallback((event: DeviceMotionEvent) => {
    const accel = event.accelerationIncludingGravity;
    if (!accel) return;

    const g = computeGForce(accel.x, accel.y, accel.z);
    if (g < thresholdRef.current) return;

    const now = Date.now();
    if (now - lastShakeAtRef.current < cooldownRef.current) return;

    // Eşik üstü hit'i kaydet (window içinde tut)
    const windowMsValue = windowMsRef.current;
    const minCount = Math.max(1, minShakeCountRef.current);

    const nextHits = [...shakeHitsRef.current, now].filter(
      (ts) => now - ts <= windowMsValue
    );
    shakeHitsRef.current = nextHits;

    if (nextHits.length < minCount) return;

    // Tetikle
    lastShakeAtRef.current = now;
    shakeHitsRef.current = [];
    onShakeRef.current();
  }, []);

  useEffect(() => {
    if (!enabled) return;
    if (!isSupported) return;

    window.addEventListener("devicemotion", handleMotion, { passive: true });
    return () => {
      window.removeEventListener("devicemotion", handleMotion);
    };
  }, [enabled, handleMotion, isSupported]);

  return {
    requestPermission: requestMotionPermission,
    isSupported: Boolean(isSupported),
  };
}
